import * as RD from '@radix-ui/react-dialog';
import * as DM from '@radix-ui/react-dropdown-menu';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Ellipsis, X } from '../icons';

/** Dialog on desktop, bottom sheet on phones (same component, CSS decides). Focus trap by Radix. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  closeLabel,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  closeLabel: string;
}) {
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="overlay" />
        <RD.Content className="dialog" aria-describedby={description ? undefined : undefined}>
          <div className="dialog-head">
            <div>
              <RD.Title asChild>
                <h2>{title}</h2>
              </RD.Title>
              {description ? (
                <RD.Description className="small muted" style={{ marginTop: 4 }}>
                  {description}
                </RD.Description>
              ) : (
                <RD.Description className="sr-only">{title}</RD.Description>
              )}
            </div>
            <RD.Close className="btn quiet icon" aria-label={closeLabel}>
              <X size={16} aria-hidden="true" />
            </RD.Close>
          </div>
          {children}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

export interface MenuItem {
  label: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

/** "…" menu for secondary actions (LG 2.4). */
export function MoreMenu({ label, items, groups }: { label: string; items?: MenuItem[]; groups?: Array<{ label?: ReactNode; items: MenuItem[] }> }) {
  const all = groups ?? [{ items: items ?? [] }];
  return (
    <DM.Root>
      <DM.Trigger className="btn quiet icon sm" aria-label={label}>
        <Ellipsis size={16} aria-hidden="true" />
      </DM.Trigger>
      <DM.Portal>
        <DM.Content className="menu" align="end" sideOffset={4}>
          {all.map((g, gi) => (
            <div key={gi}>
              {gi > 0 && <DM.Separator className="menu-sep" />}
              {g.label && <DM.Label className="menu-label label">{g.label}</DM.Label>}
              {g.items.map((it, i) => (
                <DM.Item
                  key={i}
                  className="menu-item"
                  disabled={it.disabled}
                  onSelect={it.onSelect}
                  style={it.danger ? { color: 'var(--bad)' } : undefined}
                >
                  {it.label}
                </DM.Item>
              ))}
            </div>
          ))}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

interface Toast {
  id: number;
  text: ReactNode;
  tone?: 'error';
  action?: { label: string; onClick: () => void };
}
const ToastCtx = createContext<{ push: (t: Omit<Toast, 'id'>) => void } | null>(null);

/** Non-blocking feedback, announced with aria-live. TODO(ch.10.4): "Annulla" bound to the change log undo. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-2), { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 5000);
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite" role="status">
        {items.map((t) => (
          <div key={t.id} className={['toast', t.tone ?? ''].filter(Boolean).join(' ')}>
            <span className="grow">{t.text}</span>
            {t.action && (
              <button type="button" className="btn quiet sm" onClick={t.action.onClick}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx.push;
}
